import React, { memo, useCallback } from "react";

import MenuContentView from "./MenuContentView";

import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";

/**
 * Props for MenuContentContainer component
 */
export interface MenuContentContainerProps {
  readonly watchlists: WatchlistWatchlistMetaFragment[];
  readonly selectedWatchlist: WatchlistWatchlistMetaFragment | null | undefined;
  readonly onSelect: (value: string) => void;
  readonly onClose: () => void;
  readonly onMyRosterOpen: (() => void) | undefined;
  readonly onAddWatchlistPress: () => void;
  readonly onActionsheetOpen: () => void;
  readonly loading: boolean;
  readonly isDark: boolean;
  readonly widthName: number;
  readonly bottomInset: number;
  readonly maxHeight: number;
  readonly isNative: boolean;
}

/**
 * Container component for menu content handling watchlist selection
 * Handles callback transformations for selection and close operations
 * @param root0 - Component props
 * @param root0.watchlists - Array of available watchlists
 * @param root0.selectedWatchlist - Currently selected watchlist
 * @param root0.onSelect - Callback when watchlist is selected
 * @param root0.onClose - Callback when menu should close
 * @param root0.onMyRosterOpen - Callback when My Roster is selected
 * @param root0.onAddWatchlistPress - Callback when New List is pressed
 * @param root0.onActionsheetOpen - Callback when Manage Lists is pressed
 * @param root0.loading - Whether watchlist creation is in progress
 * @param root0.isDark - Whether using dark theme
 * @param root0.widthName - Window width for responsive styling
 * @param root0.bottomInset - Bottom safe area inset
 * @param root0.maxHeight - Maximum height for dropdown
 * @param root0.isNative - Whether running on native platform
 */
const MenuContentContainer = memo(
  ({
    watchlists,
    selectedWatchlist,
    onSelect,
    onClose,
    onMyRosterOpen,
    onAddWatchlistPress,
    onActionsheetOpen,
    loading,
    isDark,
    widthName,
    bottomInset,
    maxHeight,
    isNative,
  }: MenuContentContainerProps) => {
    const handleWatchlistPress = useCallback(
      (id: string) => {
        onSelect(id);
        onClose();
      },
      [onSelect, onClose]
    );

    const handleMyRosterPress = useCallback(() => {
      onMyRosterOpen?.();
      onClose?.();
    }, [onMyRosterOpen, onClose]);

    return (
      <MenuContentView
        watchlists={watchlists}
        selectedWatchlistId={selectedWatchlist?.id}
        widthName={widthName}
        bottomInset={bottomInset}
        maxHeight={maxHeight}
        isNative={isNative}
        isDark={isDark}
        loading={loading}
        onWatchlistPress={handleWatchlistPress}
        onMyRosterPress={handleMyRosterPress}
        onAddWatchlistPress={onAddWatchlistPress}
        onManageListsPress={onActionsheetOpen}
      />
    );
  }
);

MenuContentContainer.displayName = "MenuContentContainer";

export default MenuContentContainer;
